import { Type } from 'typebox';
import type { ToolDefinition } from '@earendil-works/pi-coding-agent';
import type { SuperatomSDK } from '../index';

/**
 * Native Pi tools for the dashboard agent — replaces the HTTP script-bridge
 * (localhost:4001) that Pi previously reached by shelling out to `curl`
 * inside a bash call. Native tools give the model clean, structured
 * tool-call events (real `args`, not something scraped out of a shell
 * command string via regex — see extractQuerySql in collection.ts, which
 * this makes unnecessary going forward) — the same reason the main agent's
 * own flow (SourceAgent, MainAgent) uses native Anthropic tool-calling for
 * this instead of an HTTP layer.
 *
 * Each tool's execute() calls the same generic SDK surface the rest of this
 * collection already uses (sdk.getTools(), sdk.getComponents(),
 * sdk.callCollection()) — never a project-specific backend import — so this
 * file stays reusable across deployments, same principle as the rest of
 * dashboardAgent/.
 *
 * IMPORTANT: only a type-only import of @earendil-works/pi-coding-agent
 * (`import type`) — that package is pure ESM with no "require" export
 * condition, so any VALUE import from it (including defineTool, which
 * would otherwise be the natural way to preserve parameter-schema
 * inference) compiles to a require() call that fails at runtime with
 * "No 'exports' main defined" once this builds to CommonJS. Type-only
 * imports are fully erased at compile time, so they're safe. Each builder
 * below just returns a plain object literal with no explicit return-type
 * annotation — TypeScript infers the specific params shape from the
 * object literal itself as long as nothing widens it to ToolDefinition<any>.
 */

function textResult(text: string) {
	return { content: [{ type: 'text' as const, text }], details: undefined };
}

/**
 * Run SQL against a data source tool. Mirrors script-bridge.ts's `/query`
 * handler's local-tool path (tool.fn({sql, params, limit})) — the
 * cross-machine proxy fallback that endpoint also has isn't reachable here,
 * since dashboard-agent's own tool list already comes from sdk.getTools(),
 * which only ever contains locally-registered tools in the first place.
 */
function buildQueryTool(sdk: SuperatomSDK) {
	return {
		name: 'query',
		label: 'Query',
		description:
			'Execute a SQL query for real, against the live database, and get the actual results back. ' +
			'Use this to explore schema (INFORMATION_SCHEMA.TABLES/COLUMNS), verify a query returns what you ' +
			'expect before locking it into a widget, and check real data/row counts. Never guess a table or ' +
			'column name or what a code value means — check it here instead. toolId is the data source tool ' +
			'id given to you in the initial request (e.g. "mssql-b46c99e15b6d_query") — never a placeholder.',
		promptSnippet: 'query(toolId, sql, params?, limit?) — run SQL for real against a data source',
		parameters: Type.Object({
			toolId: Type.String({ description: 'The data source tool id given to you in the initial request.' }),
			sql: Type.String({ description: 'The SQL to execute.' }),
			params: Type.Optional(
				Type.Record(Type.String(), Type.Any(), { description: 'Named parameters referenced in the SQL as $paramName.' })
			),
			limit: Type.Optional(Type.Number({ description: 'Max rows to return.' })),
		}),
		async execute(_toolCallId: string, params: { toolId: string; sql: string; params?: Record<string, any>; limit?: number }) {
			const tool = sdk.getTools().find(t => t.id === params.toolId || t.name === params.toolId);
			if (!tool) {
				const available = sdk.getTools().map(t => t.id).join(', ') || '(none registered)';
				throw new Error(`Tool '${params.toolId}' not found. Available tool ids: ${available}`);
			}
			const result = await tool.fn({ sql: params.sql, params: params.params, limit: params.limit });
			const data = result?.data ?? result;
			const count = result?.count ?? (Array.isArray(data) ? data.length : 0);
			return textResult(JSON.stringify({ success: true, data, count }));
		},
	};
}

/**
 * Call a pre-built direct tool with structured params — mirrors
 * script-bridge.ts's `/run-tool`. Prefer this over raw SQL when a direct
 * tool exists and fits; it already has correct filters/joins baked in.
 */
function buildRunToolTool(sdk: SuperatomSDK) {
	return {
		name: 'run_tool',
		label: 'Run Tool',
		description:
			'Call a pre-built direct tool with structured params (business logic already encoded — correct ' +
			'filters/joins baked in). Prefer this over writing raw SQL whenever a direct tool exists and fits ' +
			'what you need.',
		promptSnippet: 'run_tool(toolId, params?) — call a pre-built direct tool',
		parameters: Type.Object({
			toolId: Type.String({ description: 'The direct tool\'s id.' }),
			params: Type.Optional(Type.Record(Type.String(), Type.Any(), { description: 'Params this tool accepts.' })),
		}),
		async execute(_toolCallId: string, params: { toolId: string; params?: Record<string, any> }) {
			const tool = sdk.getTools().find(t => t.id === params.toolId || t.name === params.toolId);
			if (!tool) {
				throw new Error(`Tool '${params.toolId}' not found.`);
			}
			const result = await tool.fn(params.params ?? {});
			return textResult(JSON.stringify({ success: true, result }));
		},
	};
}

/**
 * Knowledge base lookup — reached via the generic 'knowledge-base' collection
 * (sdk.callCollection) instead of directly importing kbNodesService/
 * ChromaManager, which are project-specific backend modules this SDK package
 * can't depend on. topK is hardcoded to 3 — pi was requesting up to 15, too
 * much to usefully read and bloats context.
 *
 * Global-type KB entries are NOT fetched here — they're the same regardless
 * of query, so collection.ts fetches them once and hands them over directly
 * in the initial request instead of re-querying them on every single call.
 * This tool only ever returns query-matched (semantic) results, plus a
 * user's own KB nodes if a userId is given (those vary by user, not by
 * query, but are cheap and not worth a separate upfront-fetch mechanism for).
 */
function buildKbSearchTool(sdk: SuperatomSDK) {
	return {
		name: 'kb_search',
		label: 'KB Search',
		description:
			'Search the knowledge base for domain context — business definitions, table/column meanings, ' +
			'known gotchas and warnings, correct join paths — before writing SQL. Call this for every ' +
			'distinct topic in the request (e.g. "billing" and "delivery" separately, not just one combined ' +
			'search) — a single broad query can miss a narrower, more relevant node. Returns query-matched ' +
			'(semantic) results only — global KB nodes were already given to you once in the initial request, ' +
			'not repeated here.',
		promptSnippet: 'kb_search(query, userId?) — search KB nodes (query-matched only; global nodes given upfront)',
		parameters: Type.Object({
			query: Type.String({ description: 'What to search for.' }),
			userId: Type.Optional(Type.String({ description: 'Include this user\'s own KB nodes too, if provided.' })),
		}),
		async execute(_toolCallId: string, params: { query: string; userId?: string }) {
			const results: { user: any[]; semantic: any[] } = { user: [], semantic: [] };

			if (params.userId) {
				try {
					const userResult = await sdk.callCollection<{ nodes?: any[] }>('knowledge-base', 'getByUser', { userId: params.userId, limit: 100 });
					results.user = userResult?.nodes ?? [];
				} catch (err: any) {
					console.warn(`[DashboardAgent] kb_search: user lookup failed: ${err?.message ?? err}`);
				}
			}

			try {
				const semanticResult = await sdk.callCollection<{ metadata?: { sources?: any[] }; content?: string }>(
					'knowledge-base',
					'query',
					{ prompt: params.query, topK: 3 }
				);
				results.semantic = semanticResult?.metadata?.sources ?? [];
				// The formatted content field already carries the actual node text —
				// pass it through as-is instead of re-serializing sources-only.
				if (semanticResult?.content) {
					return textResult(JSON.stringify({ success: true, user: results.user, semanticContent: semanticResult.content, semanticSources: results.semantic }));
				}
			} catch (err: any) {
				console.warn(`[DashboardAgent] kb_search: semantic query failed: ${err?.message ?? err}`);
			}

			return textResult(JSON.stringify({ success: true, ...results }));
		},
	};
}

/** The live, frontend-registered component catalog — mirrors script-bridge.ts's `/components`. */
function buildComponentsTool(sdk: SuperatomSDK) {
	return {
		name: 'components',
		label: 'Components',
		description:
			'Get the real, current component catalog — every available component with its exact prop schema ' +
			'({id, name, type, description, props, category, keywords}). Call this for every component type ' +
			'you use, every time — never assume a prop shape from memory or a similar-looking component. Real ' +
			'prop shapes are often not what you\'d expect (nested under a config object rather than flat, a ' +
			'filters array rather than options, etc.) — this is the single most common way a generated ' +
			'dashboard fails to render.',
		promptSnippet: 'components() — the real component catalog with full prop schemas',
		parameters: Type.Object({}),
		async execute() {
			return textResult(JSON.stringify({ success: true, components: sdk.getComponents() }));
		},
	};
}

/**
 * Keyword search over a data source's schema — same mechanism the main
 * agent's SourceAgent uses (source-agent.ts's search_schema tool). Every
 * tool object sdk.getTools() returns already carries a schemaSearchFn
 * function (attached in backend/src/sources/core/base-source.ts alongside
 * fn/params — it's just not part of the formal Tool zod schema, same as how
 * SourceAgent itself reads it via an `as any` cast), so no new
 * infrastructure is needed here — only large/very_large-tier sources have
 * one at all; small/medium sources leave it undefined, since
 * INFORMATION_SCHEMA queries via `query` are already fine for those.
 */
function buildSchemaSearchTool(sdk: SuperatomSDK) {
	return {
		name: 'schema_search',
		label: 'Schema Search',
		description:
			'Search a large data source\'s schema for tables and columns matching keywords — faster and more ' +
			'reliable than guessing table names to run INFORMATION_SCHEMA queries against, especially for a ' +
			'source with hundreds of tables. Only large/very-large-tier sources support this — for smaller ' +
			'sources, use the `query` tool\'s INFORMATION_SCHEMA discovery instead. Use this BEFORE writing a ' +
			'query against an unfamiliar table on a large source, to find exact table/column names first.',
		promptSnippet: 'schema_search(toolId, keywords) — keyword search over a large source\'s schema',
		parameters: Type.Object({
			toolId: Type.String({ description: 'The data source tool id given to you in the initial request.' }),
			keywords: Type.Array(Type.String(), { description: 'Keywords to search for in table/column names, e.g. ["quotation", "amount", "customer"].' }),
		}),
		async execute(_toolCallId: string, params: { toolId: string; keywords: string[] }) {
			const tool = sdk.getTools().find(t => t.id === params.toolId || t.name === params.toolId);
			if (!tool) {
				const available = sdk.getTools().map(t => t.id).join(', ') || '(none registered)';
				throw new Error(`Tool '${params.toolId}' not found. Available tool ids: ${available}`);
			}
			const schemaSearchFn = (tool as any).schemaSearchFn as ((keywords: string[]) => string) | undefined;
			if (!schemaSearchFn) {
				throw new Error(`'${params.toolId}' doesn't support schema search (only large/very-large-tier sources do) — use the query tool's INFORMATION_SCHEMA discovery instead.`);
			}
			return textResult(schemaSearchFn(params.keywords));
		},
	};
}

export function buildDashboardAgentTools(sdk: SuperatomSDK) {
	return [
		buildQueryTool(sdk),
		buildRunToolTool(sdk),
		buildSchemaSearchTool(sdk),
		buildKbSearchTool(sdk),
		buildComponentsTool(sdk),
	];
}
